import {
  GoogleMap,
  OverlayView,
  useLoadScript,
  InfoWindow,
  OverlayViewF,
} from "@react-google-maps/api";
import { useEffect, useMemo, useState } from "react";

const option = {
  zoomControl: true,
  disableDefaultUI: true,
  scrollwheel: false,
  styles: [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [
        {
          weight: "2.00",
        },
      ],
    },
    {
      featureType: "all",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#9c9c9c",
        },
      ],
    },
    {
      featureType: "all",
      elementType: "labels.text",
      stylers: [
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [
        {
          color: "#f2f2f2",
        },
      ],
    },
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [
        {
          saturation: -100,
        },
        {
          lightness: 45,
        },
      ],
    },
    {
      featureType: "road",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#eeeeee",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#7b7b7b",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels.text.stroke",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified",
        },
      ],
    },
    {
      featureType: "road.arterial",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [
        {
          color: "#46bcec",
        },
        {
          visibility: "on",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#c8d7d4",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: "#070707",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "labels.text.stroke",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },
  ],
};
const containerStyle = {
  width: "100%",
  height: "100%",
};

const DEFAULT_COORDINATE = {
  lat: 32.411201277163975,
  lng: -96.12394824867293,
};

const parseCoordinate = (value, fallback) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getIframeAttr = (html, attrName) => {
  const regex = new RegExp(`${attrName}\\s*=\\s*([\"'])(.*?)\\1`, "i");
  const match = html.match(regex);
  return match?.[2] || "";
};

const parseEmbededMap = (value) => {
  const raw = (value || "").trim();
  if (!raw) {
    return null;
  }

  if (!raw.toLowerCase().includes("<iframe")) {
    return {
      src: raw,
      width: "100%",
      height: "100%",
      allowFullScreen: true,
      loading: "lazy",
      referrerPolicy: "no-referrer-when-downgrade",
    };
  }

  const src = getIframeAttr(raw, "src");
  if (!src) {
    return null;
  }

  const loading = getIframeAttr(raw, "loading") || "lazy";
  const referrerPolicy =
    getIframeAttr(raw, "referrerpolicy") || "no-referrer-when-downgrade";
  const allowFullScreenAttr = getIframeAttr(raw, "allowfullscreen");
  const hasAllowFullScreen =
    /allowfullscreen(\s*=\s*(["'])(.*?)\2)?/i.test(raw) &&
    allowFullScreenAttr.toLowerCase() !== "false";

  return {
    src,
    width: "100%",
    height: "100%",
    allowFullScreen: hasAllowFullScreen,
    loading,
    referrerPolicy,
  };
};

export default function ContactMap({ data, lat, lng, EmbededMapURL }) {
  const [getLocation, setLocation] = useState(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyAAz77U5XQuEME6TpftaMdX0bBelQxXRlM",
  });

  const marker = useMemo(() => {
    const latitude = parseCoordinate(lat ?? data?.Latitude, DEFAULT_COORDINATE.lat);
    const longitude = parseCoordinate(lng ?? data?.Longitude, DEFAULT_COORDINATE.lng);

    return {
      lat: latitude,
      long: longitude,
      title: "Office address",
      imageSrc: "/images/banner/contact.jpg",
      info: [
        {
          iconClass: "icon icon-map-trifold",
          text: data?.Address || "Address not available",
        },
        {
          iconClass: "icon icon-phone-line",
          text: data?.ContactNo || "Contact number not available",
        },
        {
          iconClass: "icon icon-mail-line",
          text: data?.Email || "Email not available",
        },
      ],
    };
  }, [data, lat, lng]);

  const center = useMemo(
    () => ({ lat: marker.lat, lng: marker.long }),
    [marker.lat, marker.long]
  );

  useEffect(() => {
    setLocation(marker);
  }, [marker]);

  const CustomMarker = ({ elm }) => {
    return (
      <div className="marker-container" onClick={() => setLocation(elm)}>
        <div className="marker-card">
          <div className="front face">
            <div />
          </div>
          <div className="back face">
            <div />
          </div>
          <div className="marker-arrow" />
        </div>
      </div>
    );
  };

  // close handler
  const closeCardHandler = () => {
    setLocation(null);
  };

  const embededMapURL = (EmbededMapURL || data?.EmbededMapURL || "").trim();
  const parsedEmbededMap = parseEmbededMap(embededMapURL);

  if (parsedEmbededMap) {
    return (
      <iframe
        src={parsedEmbededMap.src}
        width="100%"
        height="80%"
        style={{ border: 0, display: "block" }}
        allowFullScreen={parsedEmbededMap.allowFullScreen}
        loading={parsedEmbededMap.loading}
        referrerPolicy={parsedEmbededMap.referrerPolicy}
        title="Embedded Location Map"
      />
    );
  }

  return (
    <>
      {!isLoaded ? (
        <p>Loading...</p>
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={16}
          options={option}
        >
          <OverlayViewF
            position={{
              lat: marker.lat,
              lng: marker.long,
            }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <CustomMarker elm={marker} />
          </OverlayViewF>
          {getLocation !== null && (
            <InfoWindow
              position={{
                lat: getLocation.lat,
                lng: getLocation.long,
              }}
              onCloseClick={closeCardHandler}
            >
              <div className="map-box">
                <div className="contact-map-item">
                  <div className="inner-box">
                    <div className="image-box">
                      <img src={getLocation.imageSrc} alt={getLocation.title} />
                    </div>
                    <div className="content">
                      <div className="title">{getLocation.title}</div>
                      <ul className="list-info">
                        {getLocation.info.map((item, index) => (
                          <li key={index}>
                            <span className={item.iconClass} />
                            {item.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </>
  );
}
